// ./src/config/userCarRoles.ts
// User-Car role configuration
// Defines the roles users can have for cars they're assigned to

// ============================================
// Role Constants
// ============================================

export const USER_CAR_ROLES = {
  /** Full control - can edit car settings, manage users, delete car */
  OWNER: 1,
  /** Can add/edit expenses, refuels, travels - cannot manage car settings */
  DRIVER: 2,
  /** Read-only access - can view car data but cannot make changes */
  VIEWER: 3,
} as const;

// ============================================
// Types
// ============================================

export interface UserCarRoleOption {
  /** Role ID stored in database */
  value: number;
  /** Translation key - use with t('common:userCarRoles.owner') */
  labelKey: string;
  /** Translation key for description */
  descriptionKey: string;
  /** Icon name for UI components */
  iconName: string;
}

// ============================================
// Role Options
// ============================================

export const USER_CAR_ROLE_OPTIONS: UserCarRoleOption[] = [
  {
    value: USER_CAR_ROLES.OWNER,
    labelKey: "userCarRoles.owner",
    descriptionKey: "userCarRoles.ownerDescription",
    iconName: "Crown",
  },
  {
    value: USER_CAR_ROLES.DRIVER,
    labelKey: "userCarRoles.driver",
    descriptionKey: "userCarRoles.driverDescription",
    iconName: "SteeringWheel",
  },
  {
    value: USER_CAR_ROLES.VIEWER,
    labelKey: "userCarRoles.viewer",
    descriptionKey: "userCarRoles.viewerDescription",
    iconName: "Eye",
  },
];

// ============================================
// Lookup Map
// ============================================

export const USER_CAR_ROLE_MAP: Record<number, UserCarRoleOption> = Object.fromEntries(
  USER_CAR_ROLE_OPTIONS.map((opt) => [opt.value, opt])
);

// ============================================
// Helper Functions
// ============================================

/**
 * Get role option by value
 * @param value - Role ID
 * @returns Role option or undefined
 */
export const getUserCarRoleOption = (
  value: number | null | undefined
): UserCarRoleOption | undefined => {
  if (value === null || value === undefined) return undefined;
  return USER_CAR_ROLE_MAP[value];
};

/**
 * Get role label key
 * @param value - Role ID
 * @returns Label translation key or empty string
 */
export const getUserCarRoleLabelKey = (
  value: number | null | undefined
): string => {
  const option = getUserCarRoleOption(value);
  return option?.labelKey ?? "";
};

/**
 * Get role icon name
 * @param value - Role ID
 * @returns Icon name or default
 */
export const getUserCarRoleIcon = (
  value: number | null | undefined
): string => {
  const option = getUserCarRoleOption(value);
  return option?.iconName ?? "User";
};

/**
 * Check if role has edit permissions (Owner or Driver)
 * @param roleId - Role ID
 * @returns True if can edit
 */
export const canRoleEdit = (roleId: number | null | undefined): boolean => {
  return roleId === USER_CAR_ROLES.OWNER || roleId === USER_CAR_ROLES.DRIVER;
};

/**
 * Check if role has admin permissions (Owner only)
 * @param roleId - Role ID
 * @returns True if can admin
 */
export const canRoleAdmin = (roleId: number | null | undefined): boolean => {
  return roleId === USER_CAR_ROLES.OWNER;
};